import React, {
  useContext,
  useEffect,
  useState,
  useRef,
  useMemo,
  useCallback,
  memo,
} from 'react';
import { Location } from 'history';
import { useLocation } from 'react-router';
import DelsystemList from '../../Features/Delsystem/_Common/Const/Delsystem';
import { rutiner } from '../../Features/Delsystem/_Common/Const/Rutiner';
import { rutinInfos } from '../../Features/Delsystem/_Common/Const/RutinInfo';
import { styrvarden } from '../../Features/Delsystem/_Common/Const/Styrvarden';
import {
  rutinRouteName,
  styrvardeRouteName,
  debugRouteName,
  loginRouteName,
} from '../../Features/Routing/router';
import { AvenyContextConsumer } from '../../Features/AvenyContext';
import { AppNavBarView } from './Components/AppNavBarView/AppNavBarView';
import { TaskMenuButton } from './Components/TaskMenuButton/TaskMenuButton';
import MainMenu from '../Menu/MainMenu/MainMenu';
import TaskMenu from '../Menu/TaskMenu/TaskMenu';
import MessageState from '../Messages/Components/MessageState';
import { IMessage } from '../Messages/Messages';
import Logger, { namedError } from '../../Utils/Logger';
import {
  LoginStateContext,
  IDatabase,
} from '../../Features/Login/Contexts/LoginStateContext/LoginStateContext';
import {
  databaseMissingErrorText,
  useActiveDatabaseContext,
} from '../ActiveDatabaseContext/ActiveDatabaseContextUtils';
import { MessageUpdateType } from '../Messages/Mutations/MessageListUpdateMutation';
import { MessageDeleteType } from '../Messages/Mutations/MessageListDeleteMutation';
import { useIsExtraSmallScreen } from '../../Hooks/useFullscreenView';
import { stringEqualIgnoreCase } from '../../Utils/stringUtils';
import MainMenuFab from '../FloatingBottomArea/MainMenuFab';
import { useIsTouchDevice } from '../BreakpointContext/BreakpointContextUtils';
import { useIsFloatingMenuVisible } from '../FloatingBottomArea/FloatingMenu';

interface IProps {}

interface IInnerProps extends IProps {
  isLoggedIn: boolean;
  messages: IMessage[];
  newMessages: boolean | null;
  updateMessage: MessageUpdateType;
  deleteMessage: MessageDeleteType;
  routeIsAtLogin: boolean;
  isProcesses: boolean;
  messagesLoading: boolean;
  currentPath: string;
  routineKey?: string;
}

export interface IPathInfo {
  db?: IDatabase;
  path: string;
}

const getCurrentPath = (
  location: Location
): { path: string; routineKey?: string } => {
  const path = location.pathname.split('/');
  if (path.length < 6) {
    if (path.length > 2) {
      const lastPath = path[path.length - 1].toLowerCase();

      if (lastPath === '' || (path[4] && !path[5])) {
        return { path: 'Startsida' };
      }

      if (lastPath === 'processes') {
        return { path: 'Processer' };
      }
    }

    return { path: '' };
  }

  // Är vi inne i en rutin eller styrvärde?
  const firstPathCompAfterDB = path[5];
  let routineKey;
  if (stringEqualIgnoreCase(firstPathCompAfterDB, rutinRouteName)) {
    routineKey = Object.keys(rutiner).find((x: string) =>
      stringEqualIgnoreCase(path[6], rutinInfos[x].rutinURL)
    );
  } else if (stringEqualIgnoreCase(firstPathCompAfterDB, styrvardeRouteName)) {
    routineKey = Object.keys(styrvarden()).find((x: string) =>
      stringEqualIgnoreCase(path[6], rutinInfos[x].rutinURL)
    );
  }
  if (routineKey && rutinInfos[routineKey]) {
    return { path: rutinInfos[routineKey].benamning, routineKey: routineKey };
  }

  // Är vi inne i debugrutinen?
  if (stringEqualIgnoreCase(firstPathCompAfterDB, debugRouteName)) {
    return { path: 'DebugRutin', routineKey: '-1' };
  }

  // Är vi inne i GraphiQL?
  if (stringEqualIgnoreCase(firstPathCompAfterDB, 'graphiql')) {
    return { path: 'GraphiQL', routineKey: '-1' };
  }
  // Är vi inne i ett delsystem?
  const subSystem = DelsystemList.find(subSys =>
    stringEqualIgnoreCase(subSys.path, firstPathCompAfterDB)
  );
  if (subSystem) {
    return { path: subSystem.benamning };
  }

  // Avslutande / har skapat en tom avslutande del i pathen
  if (firstPathCompAfterDB === '') {
    return { path: 'Startsida' };
  }

  // Om vi håller på att logga in via singleSignOn så returnera tom sträng, vi kommer ändå att routas om direkt
  if (path[1] === 'SingleSignOn') {
    return { path: '' };
  }

  if (stringEqualIgnoreCase(firstPathCompAfterDB, loginRouteName)) {
    return { path: '' };
  }

  Logger.error(
    namedError(
      'PathError',
      `Could not understand path: ${JSON.stringify(location.pathname)}`
    )
  );
  return { path: '' };
};

export const AppNavbar: React.FC<React.PropsWithChildren<IInnerProps>> = memo(
  props => {
    const loginStateContext = useContext(LoginStateContext);
    const isSmallScreen = useIsExtraSmallScreen();
    const mobileLayout = useIsTouchDevice();
    const { activeDatabase } = useActiveDatabaseContext();

    const searchBarRef = useRef<HTMLInputElement | null>(null);
    const miscMenuAnchor = useRef<HTMLElement>(undefined);
    const userMenuAnchorElement = useRef<HTMLElement>(undefined);
    const [userMenuOpen, setUserMenuOpen] = useState(false);
    const [miscMenuOpen, setMiscMenuOpen] = useState(false);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [rightMenuOpen, setRightMenuOpen] = useState(
      Boolean(props.newMessages)
    );
    const [taskMenuPinned, setTaskMenuPinned] = useState(false);

    const toggleMainMenu = useCallback(() => {
      setDrawerOpen(prev => !prev);
      if (searchBarRef.current && !drawerOpen) {
        searchBarRef.current.focus();
      }
    }, [drawerOpen]);

    const openMiscMenu = useCallback((event: React.MouseEvent<HTMLElement>) => {
      miscMenuAnchor.current = event.currentTarget;
      setMiscMenuOpen(true);
    }, []);

    const closeMiscMenu = useCallback(() => {
      miscMenuAnchor.current = undefined;
      setMiscMenuOpen(false);
    }, []);

    const toggleRightMenu = useCallback(() => {
      setRightMenuOpen(prev => !prev);
      closeMiscMenu();
    }, [closeMiscMenu]);

    useEffect(() => {
      if (props.newMessages && !isSmallScreen) {
        setRightMenuOpen(true);
      }
    }, [props.newMessages, isSmallScreen]);

    const onUserMenuClick = useCallback(
      (event: React.MouseEvent<HTMLElement>) => {
        userMenuAnchorElement.current = event.currentTarget;
        setUserMenuOpen(true);
      },
      []
    );

    const onUserMenuClose = useCallback(() => {
      userMenuAnchorElement.current = undefined;
      setUserMenuOpen(false);
    }, []);

    const renderTaskMenuButton = useCallback(
      (small?: boolean) => {
        return (
          <TaskMenuButton
            messages={props.messages}
            small={small}
            pinned={taskMenuPinned}
            togglePinned={toggleRightMenu}
            disabledReason={databaseMissingErrorText}
          />
        );
      },
      [props.messages, taskMenuPinned, toggleRightMenu]
    );

    const floatingTouchLayout = useIsFloatingMenuVisible();

    return (
      <AvenyContextConsumer>
        {avenyContext => (
          <>
            <AppNavBarView
              renderTaskMenuButton={renderTaskMenuButton}
              toggleRightMenu={toggleRightMenu}
              onUserMenuClick={onUserMenuClick}
              onUserMenuClose={onUserMenuClose}
              userMenuOpen={userMenuOpen}
              openMiscMenu={openMiscMenu}
              miscMenuAnchor={miscMenuAnchor.current}
              miscMenuOpen={miscMenuOpen}
              userMenuAnchorElement={userMenuAnchorElement.current}
              closeMiscMenu={closeMiscMenu}
              currentPath={props.currentPath}
              toggleMainMenu={toggleMainMenu}
              showEnhetSelector={!props.isProcesses}
              programLocationHeader={props.currentPath}
              isLoggedIn={props.isLoggedIn}
              routeIsAtLogin={props.routeIsAtLogin}
              activeDatabaseName={activeDatabase}
              routineKey={props.routineKey}
              formHasChanged={avenyContext.formHasChanged}
              floatingTouchLayout={floatingTouchLayout}
              isTouchDevice={mobileLayout}
            />
            {!props.routeIsAtLogin && (
              <React.Fragment>
                {!avenyContext.singleRoutineMode.enabled && (
                  <MainMenu
                    onToggle={toggleMainMenu}
                    databases={loginStateContext.databases}
                    open={drawerOpen}
                    activeDatabase={activeDatabase}
                    searchBarRef={searchBarRef}
                    onUserMenuClick={onUserMenuClick}
                    onToggleTaskMenu={toggleRightMenu}
                    messages={props.messages}
                    anchor={floatingTouchLayout ? 'right' : undefined}
                  />
                )}
                <TaskMenu
                  open={rightMenuOpen}
                  messages={props.messages}
                  deleteMessage={props.deleteMessage}
                  updateMessage={props.updateMessage}
                  onPinned={setTaskMenuPinned}
                  onToggle={toggleRightMenu}
                  initialMessagesTab={Boolean(props.newMessages)}
                  messagesLoading={props.messagesLoading}
                />
              </React.Fragment>
            )}
            {floatingTouchLayout &&
              !props.routeIsAtLogin &&
              !avenyContext.singleRoutineMode.enabled && (
                <MainMenuFab
                  onClick={toggleMainMenu}
                  disabled={!activeDatabase}
                />
              )}
          </>
        )}
      </AvenyContextConsumer>
    );
  }
);
AppNavbar.displayName = 'AppNavbar';
/**
 * Den övre raden i programmet som alltid visas så länge man är inloggad
 */
const EnhancedAppNavBar = (props: IProps) => {
  const loginStateContext = useContext(LoginStateContext);
  const location = useLocation();

  const [currentPath, setCurrentPath] = useState('');
  const [routineKey, setRoutineKey] = useState<string | undefined>(undefined);
  const updateCurrentPath = useCallback(() => {
    const path = getCurrentPath(location);
    setCurrentPath(path.path);
    setRoutineKey(path.routineKey);
  }, [location]);

  useEffect(updateCurrentPath, [location.pathname, updateCurrentPath]);
  // Todo: This is a temporary solution to hide databaseAvatars due to presentation.
  const isProcesses = useMemo(() => {
    const path = location.pathname.split('/');
    return path.length > 0
      ? path[path.length - 1].toLowerCase() === 'processes'
      : false;
  }, [location.pathname]);

  if (!loginStateContext.loading && !loginStateContext.routeIsAtLogin) {
    return (
      <MessageState>
        {({
          messages,
          newMessages,
          updateMessage,
          deleteMessage,
          loading: messagesLoading,
        }) => (
          <AppNavbar
            isLoggedIn={loginStateContext.isLoggedIn}
            routeIsAtLogin={loginStateContext.routeIsAtLogin}
            isProcesses={isProcesses}
            currentPath={currentPath}
            messagesLoading={messagesLoading}
            routineKey={routineKey}
            messages={messages}
            newMessages={newMessages}
            updateMessage={updateMessage}
            deleteMessage={deleteMessage}
            {...props}
          />
        )}
      </MessageState>
    );
  } else {
    return null;
  }
};

export default EnhancedAppNavBar;
